// External libraries
import { Component } from "react";

// FontAwesome icons
import { faLink } from "@fortawesome/pro-duotone-svg-icons";

// Preact Signals
import { effect, signal } from "@preact/signals-react";

// Kendo React components
import { Button } from "@progress/kendo-react-buttons";
import { Window } from "@progress/kendo-react-dialogs";
import { Input, InputSeparator, InputSuffix, TextBox } from "@progress/kendo-react-inputs";
import { Error, FloatingLabel, Hint } from "@progress/kendo-react-labels";
import { Ripple } from "@progress/kendo-react-ripple";

// Internal components
import { ColorChangingIcon } from "../../Common/UI/ColorChangingIcon";
import { If } from "../../Common/UI/IfComponent";
import { TooltipPopupIcon } from "../../Common/UI/TooltipPopupIcon";

// Utilities
import { validators } from "../../Form/validators";
import { Invisible, setStyle, validateStageStep } from "../../Utilities/EntryUtils";

// Signals
import {
  clearEntryFields,
  disabledSections,
  formErrors,
  readOnlyEntry,
  template,
} from "../../Entry/Manage/EditEntrySignals";

const hyperlinkText = signal("");
const hyperlinkHref = signal("");

export class HyperlinkField extends Component {
  constructor({
    field,
    value,
    handleFieldChange,
    sectionIndex,
    fieldIndex,
    eventIndex,
    labelVisible,
    galleryParentName,
    isStageField,
    isPrintPreview,
  }) {
    super();
    const { fieldName, properties } = field;
    const parsedValue = JSON.tryParse(value, { href: "", text: "" });
    this.state = {
      href: parsedValue.href,
      text: parsedValue.text,
      valid: this.validate(
        { href: parsedValue.href, text: parsedValue.text },
        properties?.IsRequired
      ),
      invisible: false,
      disabled: false,
      showLinkDialog: false,
      saveDisabled: true,
      touched: false,
    };
    this.fieldName = typeof eventIndex === "number" ? `${fieldName}_${eventIndex}` : fieldName;
    this.isRequired = properties?.IsRequired;
    this.isReadOnly = properties?.IsReadOnly;
    this.hint = properties?.Hint;
    this.fieldInformation = properties?.FieldInformation;
    this.visibilityRules = properties?.VisibilityRules;
    this.parameterValues = properties?.ParameterValues;
    this.label = properties?.Label || `${fieldName}:`;
    this.labelVisible = labelVisible;
    this.sectionIndex = sectionIndex;
    this.fieldIndex = fieldIndex;
    this.eventIndex = eventIndex;
    this.handleFieldChange = handleFieldChange;
    this.galleryParentName = galleryParentName;
    this.isStageField = isStageField;
    this.isPrintPreview = isPrintPreview;
    this.sectionName = template.value?.sections?.[sectionIndex]?.sectionName;
    this.validationMessage = "Error: A hyperlink is required.";
    this.disposeValidationEffect = () => {};
    this.disposeDependencyEffect = () => {};
    this.disposeClearFieldsEffect = () => {};
    this.disposeDisabledSectionsEffect = () => {};
  }

  //#region lifecycle functions
  componentDidMount() {
    this.handleClearFieldEffect();
    if (this.isRequired) {
      if (!this.validate({ href: this.state.href, text: this.state.text }, this.isRequired)) {
        const newFormErrors = { ...formErrors.value };
        newFormErrors[this.sectionName] = newFormErrors[this.sectionName] || [];
        newFormErrors[this.sectionName].push(this.fieldName);
        formErrors.value = newFormErrors;
      }
      this.handleValidationEffect();
      this.updateFormErrors();
    }
    if (this.visibilityRules) this.handleDependencyEffect();

    this.disposeDisabledSectionsEffect = effect(() => {
      const isSectionDisabled = disabledSections.value[this.sectionName];
      if (this.state.disabled !== isSectionDisabled) {
        this.setState({ disabled: isSectionDisabled });
      }
    });
  }

  componentWillUnmount() {
    this.disposeValidationEffect();
    this.disposeDependencyEffect();
    this.disposeClearFieldsEffect();
    this.disposeDisabledSectionsEffect();
  }

  shouldComponentUpdate(_, nextState) {
    return (
      this.state.href !== nextState.href ||
      this.state.text !== nextState.text ||
      this.state.valid !== nextState.valid ||
      this.state.invisible !== nextState.invisible ||
      this.state.disabled !== nextState.disabled ||
      this.state.showLinkDialog !== nextState.showLinkDialog ||
      this.state.saveDisabled !== nextState.saveDisabled ||
      this.state.touched !== nextState.touched
    );
  }

  getSnapshotBeforeUpdate(_, prevState) {
    return {
      visibilityChange: prevState.invisible && !this.state.invisible,
      hrefChange: prevState.href !== this.state.href,
      textChange: prevState.text !== this.state.text,
    };
  }

  componentDidUpdate(_, __, snapshot) {
    if (this.conditionalFormattingRules && (snapshot.hrefChange || snapshot.textChange))
      this.handleStyleEffect();
    if (this.state.invisible) this.clearField();
    if (!this.state.invisible && this.isRequired) {
      this.setState(
        (prevState) => ({ valid: this.validate(prevState.value, this.isRequired) }),
        () => {
          this.updateFormErrors();
        }
      );
    }
    if (this.state.disabled) this.clearFormErrors();
  }
  //#endregion lifecycle functions

  //#region custom functions
  validate(value, isRequired) {
    return isRequired ? validators.hyperlink(value) : true;
  }

  handleClearFieldEffect() {
    this.disposeClearFieldsEffect = effect(() => {
      if (clearEntryFields.value) {
        if (this.state.text !== "" || this.state.href !== "") {
          this.clearField();
        }
      }
    });
  }

  handleValidationEffect() {
    this.disposeValidationEffect = effect(() => {
      this.setState({
        valid: formErrors.value[this.sectionName]
          ? formErrors.value[this.sectionName]?.indexOf(this.fieldName) === -1
          : true,
      });
    });
  }

  updateFormErrors() {
    if (!this.state.valid) {
      if (
        formErrors.value[this.sectionName]?.indexOf(this.fieldName) === -1 ||
        !formErrors.value[this.sectionName]
      ) {
        const newFormErrors = { ...formErrors.value };
        newFormErrors[this.sectionName] = newFormErrors[this.sectionName] || [];
        newFormErrors[this.sectionName].push(this.fieldName);
        formErrors.value = newFormErrors;
      }
    } else {
      this.clearFormErrors();
    }
  }

  clearFormErrors() {
    const newFormErrors = { ...formErrors.value };
    const sectionErrors = newFormErrors[this.sectionName];
    if (sectionErrors) {
      newFormErrors[this.sectionName] = sectionErrors.filter((error) => error !== this.fieldName);
    }
    formErrors.value = newFormErrors;
  }

  handleDependencyEffect() {
    this.disposeDependencyEffect = effect(() => {
      const result = Invisible({
        VisibilityRules: this.visibilityRules,
        parameterValues: this.parameterValues,
        eventIndex: this.eventIndex,
        galleryName: this.galleryParentName,
      });

      this.setState({
        invisible: result.invisible && !result.disabled,
        disabled: result.invisible && result.disabled,
      });
    });
  }

  handleStyleEffect() {
    setStyle(
      this.inputRef.current,
      "input",
      this.conditionalFormattingRules,
      this.state.value,
      this.fieldName
    );
  }

  clearField() {
    this.clearFormErrors();
    this.setState({ text: "", href: "" });
    this.handleFieldChange({
      e: { target: { name: this.fieldName }, value: "" },
      sectionIndex: this.sectionIndex,
      fieldIndex: this.fieldIndex,
      eventIndex: this.eventIndex,
    });
  }

  onChange() {
    if (hyperlinkHref.value === "" && hyperlinkText.value === "") {
      this.clearField();
      this.setState({ showLinkDialog: false });
      return;
    }
    this.setState(
      {
        valid: this.validate(
          { href: hyperlinkHref.value, text: hyperlinkText.value },
          this.isRequired
        ),
        text: hyperlinkText.value,
        href: hyperlinkHref.value,
        showLinkDialog: false,
        touched: true,
      },
      () => {
        this.updateFormErrors();
        this.handleFieldChange({
          e: {
            target: { name: this.fieldName },
            value: JSON.stringify({ href: hyperlinkHref.value, text: hyperlinkText.value }),
          },
          sectionIndex: this.sectionIndex,
          fieldIndex: this.fieldIndex,
          eventIndex: this.eventIndex,
        });
        if (this.isStageField) validateStageStep();
      }
    );
  }

  onBlur() {
    this.setState({
      valid: this.validate(
        (prevState) => ({ href: prevState.href, text: prevState.text }),
        this.isRequired
      ),
      touched: true,
    });
    this.updateFormErrors();
    if (this.isStageField) validateStageStep();
  }

  getClassName() {
    if (this.isRequired) {
      if (this.state.valid) {
        return "!k-valid !k-text-transparent !k-cursor-default";
      } else {
        return "!k-invalid !k-text-transparent !k-cursor-default";
      }
    } else {
      return "!k-text-transparent !k-cursor-default";
    }
  }
  //#endregion custom functions

  inputSuffix = () => {
    return (
      <div>
        <InputSeparator
          style={{
            width: "1px",
            backgroundColor: "gainsboro",
            margin: "4px 0px",
          }}
        />
        <InputSuffix>
          <ColorChangingIcon
            title="Add Link"
            margin="4px 5px 0 0"
            icon={faLink}
            initialPrimaryColor="#a6e6ff"
            initialSecondaryColor="#0078d4"
            initialSize={24}
            hoverSize={24}
            onClick={(e) => {
              e.stopPropagation();
              hyperlinkText.value = this.state.text;
              hyperlinkHref.value = this.state.href;
              this.setState({ showLinkDialog: true });
            }}
          />
        </InputSuffix>
      </div>
    );
  };

  render() {
    if (this.state.invisible) return null;
    return (
      <Ripple>
        <div
          ref={this.inputRef}
          className="k-d-flex k-flex-col"
        >
          <div style={{ position: "relative", display: "inline-block" }}>
            {this.labelVisible ? (
              <FloatingLabel
                className="k-w-full !k-cursor-default"
                label={this.labelVisible ? this.label : null}
                editorValue={this.state.text}
                editorValid={this.state.valid}
              >
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <TextBox
                    className={this.getClassName()}
                    id={this.fieldName}
                    name={this.fieldName}
                    valid={this.isRequired ? this.state.valid : true}
                    value={this.state.text}
                    readOnly={true}
                    required={this.isRequired}
                    onBlur={() => this.onBlur()}
                    suffix={() => this.inputSuffix()}
                    disabled={
                      this.isPrintPreview ||
                      this.isReadOnly ||
                      this.state.disabled ||
                      readOnlyEntry.value
                    }
                  />
                  <a
                    href={this.state.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      position: "absolute",
                      left: "10px",
                      top: "65%",
                      transform: "translateY(-50%)",
                      textDecoration: "underline",
                      color: "blue",
                      zIndex: 1,
                    }}
                  >
                    {this.state.text}
                  </a>
                  <If
                    condition={
                      this.fieldInformation !== undefined &&
                      this.fieldInformation !== "" &&
                      this.fieldInformation !== "<p></p>"
                    }
                  >
                    <TooltipPopupIcon content={this.fieldInformation} />
                  </If>
                </div>
              </FloatingLabel>
            ) : (
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <TextBox
                  className={this.getClassName()}
                  id={this.fieldName}
                  name={this.fieldName}
                  valid={this.isRequired ? this.state.valid : true}
                  value={this.state.text}
                  readOnly={true}
                  required={this.isRequired}
                  onBlur={() => this.onBlur()}
                  suffix={() => this.inputSuffix()}
                  disabled={
                    this.isPrintPreview ||
                    this.isReadOnly ||
                    this.state.disabled ||
                    readOnlyEntry.value
                  }
                />
                <a
                  href={this.state.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    position: "absolute",
                    left: "10px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    textDecoration: "underline",
                    color: "blue",
                    zIndex: 1,
                  }}
                >
                  {this.state.text}
                </a>
                <If
                  condition={
                    this.fieldInformation !== undefined &&
                    this.fieldInformation !== "" &&
                    this.fieldInformation !== "<p></p>"
                  }
                >
                  <TooltipPopupIcon content={this.fieldInformation} />
                </If>
              </div>
            )}
          </div>
          <If condition={this.hint}>
            <Hint>{this.hint}</Hint>
          </If>
          <If condition={!this.state.valid && this.state.touched}>
            <Error>{this.validationMessage}</Error>
          </If>
        </div>
        <If condition={this.state.showLinkDialog}>
          <Window
            visible={this.state.showLinkDialog}
            onClose={() => this.setState({ showLinkDialog: false })}
            title="Add Hyperlink"
            width={400}
            height={275}
          >
            <div className="k-d-flex k-flex-col">
              <Input
                label="Text"
                defaultValue={hyperlinkText.value}
                onChange={(e) => {
                  hyperlinkText.value = e.value;
                  this.setState({ saveDisabled: !hyperlinkHref.value || !hyperlinkText.value });
                }}
              />
              <Input
                label="URL"
                defaultValue={hyperlinkHref.value}
                onChange={(e) => {
                  hyperlinkHref.value = e.value;
                  this.setState({ saveDisabled: !hyperlinkHref.value || !hyperlinkText.value });
                }}
              />
              <div>
                <Button
                  themeColor={"success"}
                  onClick={() => this.onChange()}
                  disabled={this.state.saveDisabled}
                  className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-base k-mt-4"
                >
                  Save
                </Button>
              </div>
            </div>
          </Window>
        </If>
      </Ripple>
    );
  }
}
